import React, { useEffect, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
} from '@mui/material';
import { getIntegrationImportStatus, triggerValdImport } from '../integrations.api';
import { IntegrationImportStatus, ValdImportRun } from '../integrations.types';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';

interface IntegrationImportDialogProps {
    open: boolean;
    onClose: () => void;
    integrationUuid: string;
    organizationName: string;
}

const IntegrationImportDialog: React.FC<IntegrationImportDialogProps> = ({
    open,
    onClose,
    integrationUuid,
    organizationName,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isTriggering, setIsTriggering] = useState(false);
    const [status, setStatus] = useState<IntegrationImportStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState(0);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getIntegrationImportStatus(integrationUuid);
            setStatus(response.data);
        } catch (error: any) {
            console.error('Failed to fetch import status', error);
            setError(error?.response?.data?.message || 'Failed to load import status');
        } finally {
            setIsLoading(false);
        }
    }, [integrationUuid]);

    useEffect(() => {
        if (open) {
            fetchStatus();
        }
    }, [open, fetchStatus]);

    const handleTriggerImport = async (initial: boolean) => {
        setIsTriggering(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await triggerValdImport(integrationUuid, { initial });
            setSuccessMessage(
                response.data.message ||
                    `Import triggered successfully${
                        initial ? ' (90-day backfill)' : ' (incremental)'
                    }`,
            );
            setTimeout(() => {
                fetchStatus();
            }, 2000);
        } catch (error: any) {
            console.error('Failed to trigger import', error);
            setError(error?.response?.data?.message || 'Failed to trigger import');
        } finally {
            setIsTriggering(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'failed':
                return 'error';
            case 'in_progress':
                return 'warning';
            case 'pending':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatDuration = (seconds: number | null) => {
        if (!seconds) return 'N/A';
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Import Status: {organizationName}
                <Typography variant="caption" display="block" color="text.secondary">
                    {status?.integration.tenant_id}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {successMessage && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => setSuccessMessage(null)}
                    >
                        {successMessage}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleTriggerImport(false)}
                        disabled={isTriggering || isLoading}
                    >
                        Trigger Incremental Import
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleTriggerImport(true)}
                        disabled={isTriggering || isLoading}
                    >
                        Trigger 90-Day Backfill
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchStatus}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>
                </Box>

                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : status ? (
                    <>
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Overall Statistics
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Runs
                                    </Typography>
                                    <Typography variant="h5">
                                        {status.overall_stats.total_runs}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Successful
                                    </Typography>
                                    <Typography variant="h5" color="success.main">
                                        {status.overall_stats.successful_runs}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Failed
                                    </Typography>
                                    <Typography variant="h5" color="error.main">
                                        {status.overall_stats.failed_runs}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        In Progress
                                    </Typography>
                                    <Typography variant="h5" color="warning.main">
                                        {status.overall_stats.in_progress_runs}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Records Fetched
                                    </Typography>
                                    <Typography variant="h6">
                                        {status.overall_stats.total_records_fetched.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Records Stored
                                    </Typography>
                                    <Typography variant="h6">
                                        {status.overall_stats.total_records_stored.toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Run
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDate(status.overall_stats.last_run_at)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Success
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDate(status.overall_stats.last_successful_run_at)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        <Tabs
                            value={selectedTab}
                            onChange={(e, v) => setSelectedTab(v)}
                            sx={{ mb: 2 }}
                        >
                            <Tab label="Entity Status" />
                            <Tab label="Recent Runs" />
                        </Tabs>

                        {selectedTab === 0 && (
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Entity Type</TableCell>
                                            <TableCell>Total Runs</TableCell>
                                            <TableCell>Success</TableCell>
                                            <TableCell>Failed</TableCell>
                                            <TableCell>Records Fetched</TableCell>
                                            <TableCell>Last Cursor</TableCell>
                                            <TableCell>Last Success</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {status.entity_status.map((entity) => {
                                            const totalRuns = parseInt(entity.total_runs, 10) || 0;
                                            const failedRuns =
                                                parseInt(entity.failed_runs, 10) || 0;
                                            const successfulRuns = totalRuns - failedRuns;
                                            const recordsFetched =
                                                parseInt(entity.total_records_fetched, 10) || 0;

                                            return (
                                                <TableRow key={entity.entity_type}>
                                                    <TableCell>
                                                        <Chip
                                                            label={entity.entity_type}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{totalRuns}</TableCell>
                                                    <TableCell>
                                                        <Typography color="success.main">
                                                            {successfulRuns}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography color="error.main">
                                                            {failedRuns}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {recordsFetched.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        {entity.last_cursor
                                                            ? new Date(
                                                                  entity.last_cursor,
                                                              ).toLocaleString()
                                                            : 'N/A'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        {formatDate(entity.last_success_at)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {selectedTab === 1 && (
                            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Entity Type</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Records</TableCell>
                                            <TableCell>Duration</TableCell>
                                            <TableCell>Started At</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {status.recent_runs.map((run: ValdImportRun) => (
                                            <TableRow key={run.uuid}>
                                                <TableCell>
                                                    <Chip label={run.entity_type} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={run.status}
                                                        color={getStatusColor(run.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {run.records_fetched}/{run.records_stored}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDuration(run.duration_seconds)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.7rem' }}>
                                                    {formatDate(run.started_at)}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {run.error_message || '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default IntegrationImportDialog;
